/**
 * AcceptTermsStep - First onboarding step
 *
 * User must accept:
 * - Terms of Service
 * - Acceptable Use Policy
 * - Privacy Policy (optional, inform only)
 */
import React, { useState } from 'react';
import { useOnboarding } from '../OnboardingWizard';
import { tokens } from '../../../design-system';

const AcceptTermsStep = () => {
  const { goToNextStep, updateStepData, setIsLoading } = useOnboarding();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedAUP, setAcceptedAUP] = useState(false);

  const canContinue = acceptedTerms && acceptedAUP;

  const handleContinue = () => {
    if (!canContinue) return;

    // Record acceptance with timestamp
    updateStepData('accept-terms', {
      acceptedTerms: true,
      acceptedAUP: true,
      acceptedAt: new Date().toISOString(),
    });

    goToNextStep();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.iconContainer}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={tokens.colors.primary[400]} strokeWidth="1.5">
            <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h2 style={styles.title}>Welcome to Cahoots</h2>
        <p style={styles.subtitle}>
          Before we get started, please review and accept our terms.
        </p>
      </div>

      <div style={styles.checkboxes}>
        {/* Terms of Service */}
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={styles.checkboxContent}>
            <span style={styles.checkboxText}>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Terms of Service
              </a>
            </span>
            <span style={styles.checkboxDescription}>
              Our terms govern your use of Cahoots and its services.
            </span>
          </span>
        </label>

        {/* Acceptable Use Policy */}
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={acceptedAUP}
            onChange={(e) => setAcceptedAUP(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={styles.checkboxContent}>
            <span style={styles.checkboxText}>
              I agree to the{' '}
              <a href="/aup" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Acceptable Use Policy
              </a>
            </span>
            <span style={styles.checkboxDescription}>
              Guidelines for responsible use of our AI-powered features.
            </span>
          </span>
        </label>
      </div>

      {/* Privacy notice (no checkbox needed) */}
      <div style={styles.privacyNotice}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tokens.colors.neutral[500]} strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <span>
          By continuing, you acknowledge our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
            Privacy Policy
          </a>
          .
        </span>
      </div>

      {/* Continue button */}
      <button
        onClick={handleContinue}
        disabled={!canContinue}
        style={{
          ...styles.continueButton,
          opacity: canContinue ? 1 : 0.5,
          cursor: canContinue ? 'pointer' : 'not-allowed',
        }}
      >
        Continue
      </button>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  iconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    backgroundColor: `${tokens.colors.primary[500]}15`,
    borderRadius: '16px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: tokens.colors.neutral[100],
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '15px',
    color: tokens.colors.neutral[400],
    lineHeight: '1.5',
  },
  checkboxes: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colors.neutral[850] || tokens.colors.neutral[800],
    borderRadius: '12px',
    border: `1px solid ${tokens.colors.neutral[700]}`,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    accentColor: tokens.colors.primary[500],
    cursor: 'pointer',
  },
  checkboxContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  checkboxText: {
    fontSize: '15px',
    fontWeight: '500',
    color: tokens.colors.neutral[100],
  },
  checkboxDescription: {
    fontSize: '13px',
    color: tokens.colors.neutral[500],
    lineHeight: '1.4',
  },
  link: {
    color: tokens.colors.primary[400],
    textDecoration: 'underline',
  },
  privacyNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colors.neutral[500],
    marginBottom: '24px',
    padding: '12px 16px',
    backgroundColor: tokens.colors.neutral[850] || tokens.colors.neutral[800],
    borderRadius: '8px',
  },
  continueButton: {
    width: '100%',
    padding: '16px 24px',
    backgroundColor: tokens.colors.primary[500],
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    transition: 'opacity 0.15s ease',
  },
};

export default AcceptTermsStep;
